import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';
import { useTasksByOwner, useMoveTask } from '@/hooks/useTasks';
import { ITask, TaskStatus, TASK_STATUS_LABELS, TASK_PRIORITY_COLORS } from '@/types/task';
import { PHASE_TYPE_COLORS, PhaseType } from '@/types/project';
import ProjectTaskModal from '@/components/project/ProjectTaskModal';
import { toast } from 'react-hot-toast';

// Status columns configuration
const STATUS_COLUMNS: { status: TaskStatus; labelKey: string; color: string }[] = [
  { status: 'plan', labelKey: 'kanban.plan', color: 'bg-gray-500' },
  { status: 'doing', labelKey: 'kanban.doing', color: 'bg-blue-500' },
  { status: 'review', labelKey: 'kanban.review', color: 'bg-purple-500' },
  { status: 'done', labelKey: 'kanban.done', color: 'bg-green-500' },
];

// Get phase colors based on phase type (extracted from phase_name or phase_id pattern)
const getPhaseColors = (task: ITask) => {
  // Try to determine phase type from phase_name
  const phaseName = task.phase_name?.toLowerCase() || '';

  let phaseType: PhaseType = 'custom';
  if (phaseName.includes('concept') || phaseName.includes('requirements') || phaseName.includes('design')) {
    phaseType = 'concept';
  } else if (phaseName.includes('feasibility')) {
    phaseType = 'feasibility';
  } else if (phaseName.includes('pre-bom') || phaseName.includes('pre_bom') || phaseName.includes('prebom')) {
    phaseType = 'pre_bom';
  } else if (phaseName.includes('evt')) {
    phaseType = 'evt';
  } else if (phaseName.includes('dvt')) {
    phaseType = 'dvt';
  } else if (phaseName.includes('pvt')) {
    phaseType = 'pvt';
  } else if (phaseName.includes('pilot') || phaseName === 'pp') {
    phaseType = 'pp';
  } else if (phaseName.includes('mass') || phaseName === 'mp') {
    phaseType = 'mp';
  } else if (phaseName.includes('sustain')) {
    phaseType = 'sustaining';
  }

  return PHASE_TYPE_COLORS[phaseType];
};

interface ProjectKanbanProps {
  projectId: string;
  projectName: string;
  tasks: ITask[];
  onEditTask: (task: ITask) => void;
  onMoveTask: (taskId: string, newStatus: TaskStatus, sortOrder: number) => void;
}

const ProjectKanban: React.FC<ProjectKanbanProps> = ({
  projectId,
  projectName,
  tasks,
  onEditTask,
  onMoveTask,
}) => {
  const { t } = useTranslation('work');

  // Group tasks by status
  const tasksByStatus = useMemo(() => {
    const grouped: Record<TaskStatus, ITask[]> = {
      plan: [],
      doing: [],
      review: [],
      done: [],
    };
    tasks.forEach((task) => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    });
    // Sort by sort_order
    Object.keys(grouped).forEach((status) => {
      grouped[status as TaskStatus].sort((a, b) => a.sort_order - b.sort_order);
    });
    return grouped;
  }, [tasks]);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const destStatus = destination.droppableId.replace(`${projectId}-`, '') as TaskStatus;
    const destTasks = tasksByStatus[destStatus].filter((t) => t.id !== draggableId);

    let newSortOrder: number;
    if (destTasks.length === 0) {
      newSortOrder = 1;
    } else if (destination.index === 0) {
      newSortOrder = destTasks[0].sort_order - 1;
    } else if (destination.index >= destTasks.length) {
      newSortOrder = destTasks[destTasks.length - 1].sort_order + 1;
    } else {
      const prevOrder = destTasks[destination.index - 1].sort_order;
      const nextOrder = destTasks[destination.index].sort_order;
      newSortOrder = Math.floor((prevOrder + nextOrder) / 2);
    }

    onMoveTask(draggableId, destStatus, newSortOrder);
  };

  return (
    <div className="mb-8">
      {/* Project Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
          <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{projectName}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {tasks.length} {t('kanban.tasks', 'tasks')}
          </p>
        </div>
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STATUS_COLUMNS.map((column) => (
            <div key={column.status} className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
              {/* Column Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${column.color}`} />
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {t(column.labelKey, TASK_STATUS_LABELS[column.status])}
                  </span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {tasksByStatus[column.status].length}
                </span>
              </div>

              {/* Droppable Area */}
              <Droppable droppableId={`${projectId}-${column.status}`}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-[120px] space-y-2 rounded-lg transition-colors ${
                      snapshot.isDraggingOver ? 'bg-gray-200 dark:bg-gray-700' : ''
                    }`}
                  >
                    {tasksByStatus[column.status].map((task, index) => {
                      const phaseColors = getPhaseColors(task);
                      return (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => onEditTask(task)}
                              className={`p-3 bg-white dark:bg-gray-900 rounded-lg border-l-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
                                snapshot.isDragging ? 'shadow-lg ring-2 ring-emerald-500' : ''
                              }`}
                              style={{
                                ...provided.draggableProps.style,
                                borderLeftColor: phaseColors.dark,
                              }}
                            >
                              {/* Task Name */}
                              <p className="text-sm font-medium text-gray-900 dark:text-white mb-2 line-clamp-2">
                                {task.task_name}
                              </p>

                              {/* Phase Badge */}
                              {task.phase_name && (
                                <span
                                  className={`inline-block px-2 py-0.5 text-xs font-medium rounded mb-2 ${phaseColors.bg} ${phaseColors.border} border`}
                                >
                                  {task.phase_name}
                                </span>
                              )}

                              {/* Meta Info */}
                              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span className={`px-1.5 py-0.5 rounded ${TASK_PRIORITY_COLORS[task.priority]}`}>
                                  {task.priority}
                                </span>
                                {task.planned_end && (
                                  <span>{new Date(task.planned_end).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};

// Tasks without project
interface UnassignedKanbanProps {
  tasks: ITask[];
  onEditTask: (task: ITask) => void;
  onMoveTask: (taskId: string, newStatus: TaskStatus, sortOrder: number) => void;
}

const UnassignedKanban: React.FC<UnassignedKanbanProps> = ({
  tasks,
  onEditTask,
  onMoveTask,
}) => {
  const { t } = useTranslation('work');

  // Group tasks by status
  const tasksByStatus = useMemo(() => {
    const grouped: Record<TaskStatus, ITask[]> = {
      plan: [],
      doing: [],
      review: [],
      done: [],
    };
    tasks.forEach((task) => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    });
    Object.keys(grouped).forEach((status) => {
      grouped[status as TaskStatus].sort((a, b) => a.sort_order - b.sort_order);
    });
    return grouped;
  }, [tasks]);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const destStatus = destination.droppableId.replace('unassigned-', '') as TaskStatus;
    const destTasks = tasksByStatus[destStatus].filter((t) => t.id !== draggableId);

    let newSortOrder: number;
    if (destTasks.length === 0) {
      newSortOrder = 1;
    } else if (destination.index === 0) {
      newSortOrder = destTasks[0].sort_order - 1;
    } else if (destination.index >= destTasks.length) {
      newSortOrder = destTasks[destTasks.length - 1].sort_order + 1;
    } else {
      const prevOrder = destTasks[destination.index - 1].sort_order;
      const nextOrder = destTasks[destination.index].sort_order;
      newSortOrder = Math.floor((prevOrder + nextOrder) / 2);
    }

    onMoveTask(draggableId, destStatus, newSortOrder);
  };

  if (tasks.length === 0) return null;

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('kanban.unassignedTasks', 'Unassigned Tasks')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {tasks.length} {t('kanban.tasks', 'tasks')}
          </p>
        </div>
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STATUS_COLUMNS.map((column) => (
            <div key={column.status} className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${column.color}`} />
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {t(column.labelKey, TASK_STATUS_LABELS[column.status])}
                  </span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {tasksByStatus[column.status].length}
                </span>
              </div>

              <Droppable droppableId={`unassigned-${column.status}`}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-[120px] space-y-2 rounded-lg transition-colors ${
                      snapshot.isDraggingOver ? 'bg-gray-200 dark:bg-gray-700' : ''
                    }`}
                  >
                    {tasksByStatus[column.status].map((task, index) => (
                      <Draggable key={task.id} draggableId={task.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            onClick={() => onEditTask(task)}
                            className={`p-3 bg-white dark:bg-gray-900 rounded-lg border-l-4 border-gray-400 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
                              snapshot.isDragging ? 'shadow-lg ring-2 ring-emerald-500' : ''
                            }`}
                          >
                            <p className="text-sm font-medium text-gray-900 dark:text-white mb-2 line-clamp-2">
                              {task.task_name}
                            </p>
                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                              <span className={`px-1.5 py-0.5 rounded ${TASK_PRIORITY_COLORS[task.priority]}`}>
                                {task.priority}
                              </span>
                              {task.planned_end && (
                                <span>{new Date(task.planned_end).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};

const TeamworkKanban = () => {
  const { t } = useTranslation('work');
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ITask | null>(null);
  const [selectedProject, setSelectedProject] = useState<{ id: string; name: string } | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<{ id: string; name: string } | null>(null);

  // Use the logged-in user's name to fetch their tasks from Firestore
  const ownerName = user?.name || user?.username || '';
  const { data, isLoading, error, refetch } = useTasksByOwner(ownerName, true);
  const moveTaskMutation = useMoveTask();

  const allTasks = data?.tasks || [];

  // Group tasks by project
  const tasksByProject = useMemo(() => {
    const grouped: Record<string, { name: string; tasks: ITask[] }> = {};
    const unassigned: ITask[] = [];

    allTasks.forEach((task) => {
      if (task.project_id && task.project_name) {
        if (!grouped[task.project_id]) {
          grouped[task.project_id] = { name: task.project_name, tasks: [] };
        }
        grouped[task.project_id].tasks.push(task);
      } else {
        unassigned.push(task);
      }
    });

    return { projects: grouped, unassigned };
  }, [allTasks]);

  const handleEditTask = (task: ITask) => {
    setEditingTask(task);
    if (task.project_id && task.project_name) {
      setSelectedProject({ id: task.project_id, name: task.project_name });
    } else {
      setSelectedProject(null);
    }
    if (task.phase_id && task.phase_name) {
      setSelectedPhase({ id: task.phase_id, name: task.phase_name });
    } else {
      setSelectedPhase(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTask(null);
    setSelectedProject(null);
    setSelectedPhase(null);
    refetch();
  };

  const handleMoveTask = async (taskId: string, newStatus: TaskStatus, sortOrder: number) => {
    try {
      const result = await moveTaskMutation.mutateAsync({
        id: taskId,
        data: { status: newStatus, sort_order: sortOrder },
      });
      if (result.warning) {
        toast(result.warning, {
          icon: '⚠️',
          duration: 4000,
          style: { background: '#FEF3C7', color: '#92400E' },
        });
      }
    } catch (err) {
      toast.error(t('kanban.moveError', 'Failed to move task'));
      logger.error('Move task error:', err);
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-600 dark:text-red-400">
          {t('kanban.loadError', 'Failed to load tasks. Please try again later.')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const projectIds = Object.keys(tasksByProject.projects);
  const hasNoTasks = allTasks.length === 0;

  return (
    <div>
      {/* Summary Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('kanban.myTasks', 'My Tasks')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {ownerName} - {allTasks.length} {t('kanban.totalTasks', 'total tasks')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {projectIds.length} {t('kanban.projects', 'projects')}
          </span>
        </div>
      </div>

      {/* Empty State */}
      {hasNoTasks && (
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('kanban.noTasks', 'No tasks assigned')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t('kanban.noTasksDescription', 'Tasks assigned to you will appear here.')}
          </p>
        </div>
      )}

      {/* Project Kanbans */}
      {projectIds.map((projectId) => (
        <ProjectKanban
          key={projectId}
          projectId={projectId}
          projectName={tasksByProject.projects[projectId].name}
          tasks={tasksByProject.projects[projectId].tasks}
          onEditTask={handleEditTask}
          onMoveTask={handleMoveTask}
        />
      ))}

      {/* Unassigned Tasks Kanban */}
      <UnassignedKanban
        tasks={tasksByProject.unassigned}
        onEditTask={handleEditTask}
        onMoveTask={handleMoveTask}
      />

      {/* Task Modal */}
      {isModalOpen && (
        <ProjectTaskModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          task={editingTask}
          allTasks={allTasks}
          projectId={selectedProject?.id || ''}
          projectName={selectedProject?.name || ''}
          phaseId={selectedPhase?.id || ''}
          phaseName={selectedPhase?.name || ''}
        />
      )}
    </div>
  );
};

export default TeamworkKanban;
